import type { AnnouncementSummary } from '../types';

/** Formats using the date's own UTC calendar fields, not the viewer's local timezone — otherwise a UTC
 * midnight timestamp silently shifts a day earlier for any viewer west of UTC (confirmed live: "July 13"
 * rendered as "July 12" during Playwright's own visual-regression run). The same published date must read
 * identically for every viewer regardless of where they are. */
function formatAnnouncementDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** `showBody` is optional and defaults to `false` — the standalone `/dashboard` keeps today's exact
 * title/date-only row with no change. Only a caller that explicitly opts in (EPN-42's Admin Dashboard)
 * also renders the real, already-present `body` field. Category/tag chips and a contextual action
 * destination are NOT added here — no such fields exist on the backend `Announcement` model yet; that
 * remains a documented, undocumented-here backend gap rather than fabricated content. */
export function AnnouncementsPanel({ announcements, showBody = false }: { announcements: AnnouncementSummary[]; showBody?: boolean }) {
  return (
    <section className="ed-section-card">
      <div className="ed-section-header ed-announcements-header">
        <div className="ed-section-header-left">
          <span className="ed-section-icon ed-announcements-icon" aria-hidden="true">📣</span>
          <h2 className="ed-section-title ed-announcements-title">Announcements & Updates</h2>
        </div>
      </div>

      {announcements.length === 0 && (
        <div className="ed-empty-state">No new announcements or updates at this time.</div>
      )}

      {announcements.length > 0 && (
        <div className="ed-announcements-list">
          {announcements.map((a) => (
            <div className="ed-announcement-row" key={a.id}>
              {showBody && a.body ? (
                <div className="ed-announcement-titlegroup">
                  <p className="ed-announcement-title">{a.title}</p>
                  <p className="ed-announcement-body">{a.body}</p>
                </div>
              ) : (
                <p className="ed-announcement-title">{a.title}</p>
              )}
              <span className="ed-announcement-date">{formatAnnouncementDate(a.publishedAtUtc)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
